/**
 * "Libre este mes": con cuánta plata cuentas de verdad.
 *
 * ── LA PREGUNTA QUE RESPONDE ────────────────────────────────────────────────
 *
 * El balance (lo que tienes menos TODO lo que debes) contesta "¿cómo estoy si
 * pagara todo hoy?". Pero nadie paga hoy el millón que debe en Addi: paga la
 * cuota. Con $680.000 en el banco y una cuota de $150.000, el balance dice
 * −$320.000, y lo que de verdad hay para el mes es $530.000.
 *
 * Aquí se calcula esa segunda cifra: la plata en cuentas menos lo que falta
 * pagar ESTE MES. Esteban lo pidió con esas palabras: "no debe ser −320.000
 * sino 530.000 a favor".
 *
 * ── QUÉ SE RESTA Y QUÉ NO ───────────────────────────────────────────────────
 *
 *   SÍ   las cuotas de este mes que faltan, y las atrasadas
 *   SÍ   los fijos que sacan plata de una cuenta (débito o pago a mano)
 *   SÍ   lo que le debes a alguien con fecha acordada este mes
 *   SÍ   la seguridad social del contrato, si este mes no se ve pagada
 *   NO   los fijos que se cobran a la tarjeta: se pagan con la factura
 *   NO   lo que te deben: cuenta cuando llegue, no antes
 *   NO   lo ya pagado: nunca se resta dos veces
 */

import { estadoPlan, planDeCuotas, paramsDe } from './cuotas.js';
import { fechasDeCobro, idDelCobro, COBRO } from './recurrentes.js';
import { estadoDe as estadoDeuda } from './deudas.js';
import { aportesOps } from './parametros.js';

const LIQUIDAS = ['efectivo', 'ahorros', 'corriente', 'billetera'];

/** La plata que hay en efectivo, bancos y billeteras. */
export function disponibleEn(cuentas = [], saldos = {}) {
  return cuentas
    .filter((c) => LIQUIDAS.includes(c.tipo) && !c.archivada && !c.borrado_en)
    .reduce((t, c) => t + (saldos[c.id] ?? 0), 0);
}

function finDeMes(hoy) {
  const [a, m] = hoy.split('-').map(Number);
  return `${hoy.slice(0, 7)}-${String(new Date(a, m, 0).getDate()).padStart(2, '0')}`;
}

/** ¿Se ve pagada la seguridad social este mes? Por categoría o por concepto. */
function seguridadSocialPagada(movimientos, categorias, mes) {
  const cats = new Set(
    categorias.filter((c) => /eps|pensi|arl|seguridad social|pila/i.test(c.nombre)).map((c) => c.id)
  );
  return movimientos.some((m) =>
    m.tipo === 'egreso' && !m.borrado_en && m.fecha?.startsWith(mes) &&
    (cats.has(m.categoria_id) ||
     /seguridad social|pila|aportes|\beps\b|pensi[oó]n/i.test(`${m.concepto ?? ''} ${m.contraparte ?? ''}`)));
}

/**
 * Lo que falta pagar este mes, renglón por renglón, para poder mostrarlo:
 * una cifra sin su desglose no se puede revisar, y una que no se puede
 * revisar no se cree.
 */
export function compromisosDelMes({
  compras = [], abonos = [], tarjetas = [], recurrentes = [], prestamos = [],
  contratos = [], movimientos = [], categorias = [], hoy,
}) {
  const fin = finDeMes(hoy);
  const mes = hoy.slice(0, 7);
  const items = [];

  // 1. Cuotas de este mes que faltan, y las atrasadas de antes.
  for (const c of compras) {
    if (c.borrado_en || c.pagada) continue;
    const plan = c.plan?.length
      ? c.plan
      : planDeCuotas(paramsDe(c, tarjetas.find((t) => t.cuenta_id === c.cuenta_id) ?? null));
    const propios = abonos.filter((a) => a.compra_id === c.id && !a.borrado_en);
    for (const f of estadoPlan(plan, propios, hoy).filas) {
      if (f.pagada || f.fecha > fin) continue;
      items.push({
        tipo: 'cuota', nombre: `${c.descripcion} · cuota ${f.numero} de ${plan.length}`,
        monto: f.pendiente, fecha: f.fecha, vencida: f.fecha < hoy,
      });
    }
  }

  // 2. Fijos que sacan plata de una cuenta. Los de tarjeta se pagan con la factura.
  const ids = new Set(movimientos.filter((m) => !m.borrado_en).map((m) => m.id));
  for (const r of recurrentes) {
    if (r.borrado_en || r.cobro === COBRO.TARJETA) continue;
    // Lo que se debita solo y ya pasó, ya se anotó solo: se cuenta desde hoy.
    // Lo que se paga a mano puede estar atrasado: se cuenta desde que se anotó.
    const desde = r.cobro === COBRO.MANUAL
      ? (r.registrada_en && r.registrada_en > r.fecha_inicio ? r.registrada_en : r.fecha_inicio)
      : hoy;
    for (const f of fechasDeCobro(r, desde, fin)) {
      if ((r.saltados ?? []).includes(f) || ids.has(idDelCobro(r, f))) continue;
      items.push({ tipo: 'fijo', nombre: r.nombre, monto: r.monto, fecha: f, vencida: f < hoy });
    }
  }

  // 3. Lo que le debes a alguien con fecha acordada este mes, o ya pasada.
  for (const d of prestamos) {
    if (d.borrado_en || d.me_deben || estadoDeuda(d) !== 'activa') continue;
    if (!d.proxima_fecha_pago || d.proxima_fecha_pago > fin || !(d.saldo_pendiente > 0)) continue;
    items.push({
      tipo: 'deuda', nombre: `Pagarle a ${d.acreedor}`, monto: d.saldo_pendiente,
      fecha: d.proxima_fecha_pago, vencida: d.proxima_fecha_pago < hoy,
    });
  }

  // 4. La seguridad social del contrato, si este mes no se ve pagada.
  const contrato = contratos.find((x) =>
    x.activo && !x.borrado_en && x.valor_mensual > 0 && (!x.fecha_fin || x.fecha_fin >= `${mes}-01`));
  if (contrato && !seguridadSocialPagada(movimientos, categorias, mes)) {
    items.push({
      tipo: 'seguridad_social', nombre: 'Seguridad social del mes',
      monto: aportesOps(contrato.valor_mensual, hoy).total, fecha: null, vencida: false,
      nota: 'No la veo pagada este mes. Si ya la pagaste, ponle "seguridad social" en el concepto o la categoría Aporte a EPS.',
    });
  }

  items.sort((a, b) => (a.fecha ?? '9999').localeCompare(b.fecha ?? '9999'));
  return { items, total: items.reduce((t, x) => t + x.monto, 0) };
}
